import React, { useCallback, useEffect, useState } from "react";
import { styled } from "../../stitches.config";

import { bodyLarge } from "../commonStyles/Fonts";
import { h2 } from "../commonStyles/Fonts";

import DashboardHeader from "../DashboardHeader";
import TagButton from "../TagButton";
import AuthorBlock from '../AuthorBlock';

import { useAuth } from "../../context/AuthContext";
import DashboardModal, { DashboardModalType } from "./DashboardModal";
import { Author as AuthorAPI, getAuthors } from "../../api/authors";
import { Category, getCategories } from "../../api/categories";
import { getTags, Tag } from "../../api/tags";

const StarButton = React.lazy(() => import(/* webpackChunkName: "StarButton" */"../StarButton"));

const getByName = (findName: string, arr: { name: string; url: string }[]) => {
  return arr.find(({ name }) => name === findName);
};

export enum FOLLOWED_TYPE {
  FOLLOWED_AUTHORS = "followed_authors",
  FOLLOWED_CATEGORIES = "followed_categories",
  FOLLOWED_TAGS = "followed_tags",
}

const DashboardWatched: React.FC = () => {
  const [{ user }, dispatch] = useAuth();
  const [modalType, setModalType] = useState<DashboardModalType | null>(null);
  const [authors, setAuthors] = useState<AuthorAPI[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [modalSelected, setModalSelected] = useState<number[]>([]);

  useEffect(() => {
    setModalSelected([]);
  }, [modalType]);

  useEffect(() => {
    getAuthors().then((data) => setAuthors(data));
    getCategories().then((data) => setCategories(data));
    getTags().then((data) => setTags(data));
  }, []);

  const onAddClick = (type: DashboardModalType) => {
    setModalType(type);
  };

  const onToggleFollow = (type: FOLLOWED_TYPE, id: number) => {
    const followedIds = Object.values(user?.settings[type] ?? {});

    const addOrRemove = (array: number[], value: number) => {
      var index = array.indexOf(value);

      if (index === -1) {
        array.push(value);
      } else {
        array.splice(index, 1);
      }
      return array.map((str) => +str);
    };

    dispatch({
      type: "update_user",
      // @ts-ignore
      payload: {
        ...user,
        settings: {
          ...user!.settings,
          [type]: addOrRemove(followedIds, id),
        },
      },
    });
  };

  const onModalSave = (modalType: DashboardModalType) => {
    let type: FOLLOWED_TYPE | null = null;
    switch (modalType) {
      case DashboardModalType.CATEGORY:
        type = FOLLOWED_TYPE.FOLLOWED_CATEGORIES;
        break;
      case DashboardModalType.TAGS:
        type = FOLLOWED_TYPE.FOLLOWED_TAGS;
        break;
      case DashboardModalType.AUTHOR:
        type = FOLLOWED_TYPE.FOLLOWED_AUTHORS;
        break;
    }

    if (type) {
      return dispatch({
        type: "update_user",
        // @ts-ignore
        payload: {
          ...user,
          settings: {
            ...user!.settings,
            [type]: modalSelected,
          },
        },
      });
    }
  };

  const onToggleModal = (type: FOLLOWED_TYPE, id: number) => {
    const followedIds = modalSelected.length
      ? modalSelected
      : Object.values(user?.settings[type] ?? {});
    const arr = [...followedIds];
    const addOrRemove = (array: number[], value: number) => {
      var index = array.indexOf(value);

      if (index === -1) {
        array.push(value);
      } else {
        array.splice(index, 1);
      }
      return array.map((str) => +str);
    };

    setModalSelected(addOrRemove(arr, id));
  };

  const renderModalContent = useCallback(() => {
    switch (modalType) {
      case DashboardModalType.CATEGORY:
        return (
          <>
            {categories.map(({ name, OID }) => (
              <StarButton
                key={`star_${name}`}
                text={name}
                forceActive
                active={
                  !!modalSelected.length ||
                  Object.keys(user?.dictionary.category ?? {}).includes(
                    String(OID)
                  )
                }
                onToggle={() =>
                  onToggleModal(FOLLOWED_TYPE.FOLLOWED_CATEGORIES, OID)
                }
              />
            ))}
          </>
        );
      case DashboardModalType.TAGS:
        return (
          <>
            {tags.map(({ name, OID }) => (
              <TagButton
                name={name}
                active={
                  Object.keys(user?.dictionary.tag ?? {}).includes(
                    String(OID)
                  ) || modalSelected.includes(OID)
                }
                onToggle={() => onToggleModal(FOLLOWED_TYPE.FOLLOWED_TAGS, OID)}
              />
            ))}
          </>
        );
      case DashboardModalType.AUTHOR:
        return (
          <>
            {authors.map(({ name, img, OID }) => (
              <AuthorBlock
                subscribedAuthor={
                  Object.keys(user?.dictionary.author ?? {}).includes(
                    String(OID)
                  ) || modalSelected.includes(OID)
                }
                onToggle={() =>
                  onToggleModal(FOLLOWED_TYPE.FOLLOWED_AUTHORS, OID)
                }
                isDashboard
                author={{
                  name,
                  img,
                  url: getByName(name, authors)?.url || "",
                }}
              />
            ))}
          </>
        );
    }
  }, [modalType, user, modalSelected]);

  return (
    <>
      {modalType && (
        <DashboardModal
          type={modalType}
          onCloseBtn={() => setModalType(null)}
          onSave={() => onModalSave(modalType)}
        >
          {renderModalContent()}
        </DashboardModal>
      )}
      <StyledDashboardWrapper id="obserwowane">
        <h2 className={h2()}>Obserwowane</h2>
        <p className={bodyLarge()}>
          Tutaj znajdziesz swoje obserwowane kategorie, tematy i redaktorów.{" "}
          <br />
          Twoje zainteresowania tworzą Twój spersolnliazowany feed.{" "}
        </p>
        <DashboardBox>
          <DashboardHeader
            title="Kategorie"
            btnText="Dodaj kategorie"
            btnActive
            onBtnClick={() => onAddClick(DashboardModalType.CATEGORY)}
          />
          {user?.settings?.followed_categories?.length ? (
            <DashboardBoxWrapper>
              {user.settings.followed_categories.map((id) => (
                <StarButton
                  key={user.dictionary.category[id]?.name}
                  text={user.dictionary.category[id]?.name}
                  active
                  onToggle={() =>
                    onToggleFollow(FOLLOWED_TYPE.FOLLOWED_CATEGORIES, id)
                  }
                />
              ))}
            </DashboardBoxWrapper>
          ) : (
            <p>Jeszcze nic nie obserwujesz</p>
          )}
        </DashboardBox>
        <DashboardBox>
          <DashboardHeader
            title="Tagi"
            btnText="Dodaj tagi"
            btnActive
            onBtnClick={() => onAddClick(DashboardModalType.TAGS)}
          />
          {user?.settings?.followed_tags?.length ? (
            <DashboardBoxWrapper className="tags">
              {user.settings.followed_tags?.map((id) => (
                <TagButton
                  key={user.dictionary.tag[id]?.name}
                  name={user.dictionary.tag[id]?.name}
                  active
                  onToggle={() =>
                    onToggleFollow(FOLLOWED_TYPE.FOLLOWED_TAGS, id)
                  }
                />
              ))}
            </DashboardBoxWrapper>
          ) : (
            <p>Jeszcze nic nie obserwujesz</p>
          )}
        </DashboardBox>
        <DashboardBox>
          <DashboardHeader
            title="Autorzy"
            btnText="Dodaj autorów"
            btnActive
            onBtnClick={() => onAddClick(DashboardModalType.AUTHOR)}
          />
          {user?.settings?.followed_authors.length ? (
            <GridAuthor>
              {user.settings.followed_authors.map((id) => (
                <AuthorBlock
                  key={user.dictionary.author[id]?.name}
                  author={user.dictionary.author[id]}
                  subscribedAuthor
                  onToggle={() =>
                    onToggleFollow(FOLLOWED_TYPE.FOLLOWED_AUTHORS, id)
                  }
                  isDashboard
                />
              ))}
            </GridAuthor>
          ) : (
            <p>Jeszcze nic nie obserwujesz</p>
          )}
        </DashboardBox>
      </StyledDashboardWrapper>
    </>
  );
};

const StyledDashboardWrapper = styled("div", {
  marginBottom: "32px",
  "@bp4": {
    marginBottom: "80px",
  },
  "& h2": {
    color: "$grayscale90",
    margin: "0 16px 16px 16px",
    "@bp4": {
      margin: "0 0 16px 0",
    },
  },
  "& p": {
    margin: "0 16px 24px 16px",
    color: "$grayscale75",
    "@bp4": {
      margin: "0 0 24px 0",
    },
  },
});

const DashboardBoxWrapper = styled("div", {
  width: "100%",
  display: "flex",
  flexWrap: "wrap",
  color: "$grayscale100",
  justifyContent: "space-between",
  "& div": {
    marginBottom: "24px",
    width: "100%",
    "@bp4": {
      width: "324px",
    },
  },
  "&.tags": {
    justifyContent: "normal",

    gap: "16px",
    "& div": {
      width: "auto",
      marginBottom: "unset",
    },
  },
  "@bp4": {
    paddingBottom: "8px",
    "&.tags": {
      paddingBottom: "32px",
    },
  },
});

const GridAuthor = styled("div", {
  display: "grid",
  gap: "16px",
  width: "100%",
  gridTemplateColumns: "1fr",
  "& > div": {
    width: "100%",
  },
  "@bp4": {
    gridTemplateColumns: "1fr 1fr",
    paddingBottom: "32px",
  },
});

const DashboardBox = styled("div", {
  background: "$grayscale0",
  borderBottom: "1px solid $grayscale10",
  padding: "0 16px 40px 16px",
  display: "flex",
  flexWrap: "wrap",
  "@bp4": {
    padding: "0px 32px 0 32px",
    marginBottom: "16px",
    borderBottom: "0px solid $grayscale10",
  },
});

export default DashboardWatched;
